import React, { useState, useMemo } from "react";
import { Button, Modal, Alert, Form, InputGroup, Badge } from "react-bootstrap";
import {
  IconPlus,
  IconSearch,
  // IconFilter,
  IconPrinter,
} from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { usePurchaseOrder } from "../context/PurchaseOrderContext";
import PurchaseOrderForm from "../components/PurchaseOrderForm";
import PurchaseOrderTable from "../components/PurchaseOrderTable";
import { useDeleteData } from "hooks/useApiHooks";
import Swal from "sweetalert2";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import PrintJson from "utils/printJson";
import { formatNumber, getContrastColor } from "utils/utils";
import { API_URL } from "utils/constants";
import { WarehouseProvider } from "../context/WarehouseContext";
import { WorkshopSupplierProvider } from "../context/WorkshopSupplierContext";
import { InventoryProvider } from "../context/InventoryContext";
import { CategoryProvider } from "../context/CategoryContext";
import html2pdf from "html2pdf.js";
import WorkshopTopbar from "../components/WorkshopTopbar";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "ordered", label: "Ordered" },
  { value: "rejected", label: "Rejected" },
];

export default function PurchaseOrdersPage() {
  const {
    purchaseOrders,
    // statistics,
    filterOptions,
    handleSearch,
    handleStatusFilter,
    handleDateFromFilter,
    handleDateToFilter,
    refetch,
    refetchStatistics,
    Pagination,
  } = usePurchaseOrder();

  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);

  const { deleteData } = useDeleteData("/purchase-orders", "purchase-orders");

  // Use API statistics from purchaseOrders summary
  const stats = useMemo(() => {
    const summary = purchaseOrders.summary;

    if (!summary) {
      return {
        total: { count: 0, amount: "0.00" },
        pending: { count: 0, amount: "0.00" },
        ordered: { count: 0, amount: "0.00" },
        partially_received: { count: 0, amount: "0.00" },
        completed: { count: 0, amount: "0.00" },
        rejected: { count: 0, amount: "0.00" },
        total_amount: "0.00",
      };
    }

    return {
      total: {
        count: summary.all?.total_data || 0,
        amount: summary.all?.total_amount?.toString() || "0.00"
      },
      pending: {
        count: summary.pending?.total_data || 0,
        amount: summary.pending?.total_amount?.toString() || "0.00"
      },
      ordered: {
        count: summary.ordered?.total_data || 0,
        amount: summary.ordered?.total_amount?.toString() || "0.00"
      },
      partially_received: {
        count: summary.partially_received?.total_data || 0,
        amount: summary.partially_received?.total_amount?.toString() || "0.00"
      },
      completed: {
        count: summary.completed?.total_data || 0,
        amount: summary.completed?.total_amount?.toString() || "0.00"
      },
      rejected: {
        count: summary.rejected?.total_data || 0,
        amount: summary.rejected?.total_amount?.toString() || "0.00"
      },
      total_amount: summary.all?.total_amount?.toString() || "0.00",
    };
  }, [purchaseOrders.summary]);

  const handleCreateOrder = () => {
    setSelectedOrder(null);
    setShowModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleViewOrder = (order) => {
    setViewOrder(order);
    setShowViewModal(true);
  };

  const handleDeleteOrder = async (order) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete purchase order "${order.order_number}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteData(order.id);
        Swal.fire("Deleted!", "Purchase order has been deleted.", "success");
        refetch();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete purchase order.", "error");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewOrder(null);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setSelectedOrder(null);
    refetch();
    refetchStatistics();
    Swal.fire("Success!", "Purchase order has been saved.", "success");
  };

  const handleFormError = (error) => {
    Swal.fire(
      "Error!",
      error.message || "Failed to save purchase order.",
      "error"
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleApproveOrder = async (order) => {
    const result = await Swal.fire({
      title: "Approve Purchase Order",
      text: `Are you sure you want to approve purchase order "${order.po_number}"?`,
      icon: "question",
      input: "textarea",
      inputPlaceholder: "Enter approval notes (optional)",
      inputAttributes: {
        "aria-label": "Approval notes",
      },
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, approve it!",
      inputValidator: (value) => {
        if (!value) {
          return "Approval notes are required!";
        }
      },
    });

    if (result.isConfirmed) {
      try {
        const approvalNotes = result.value;
        const userData = localStorage.getItem("user");
        const user = userData ? JSON.parse(userData) : null;

        const payload = {
          approved_by: user?.id || "",
          approval_notes: approvalNotes,
          approval_date: new Date().toISOString(),
        };

        const response = await fetch(
          `${API_URL}/purchase-orders/${order.id}/approve`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {}
          
          if (errorData?.errors) {
            const errorMessages = Object.values(errorData.errors).flat().join('\\n');
            throw new Error(errorMessages);
          }
          throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        }

        // const data = await response.json();
        Swal.fire("Approved!", "Purchase order has been approved.", "success");
        refetch();
        refetchStatistics();
      } catch (error) {
        console.error("Error approving purchase order:", error);
        Swal.fire("Error!", error.message || "Failed to approve purchase order.", "error");
      }
    }
  };

  const handleRejectOrder = async (order) => {
    const result = await Swal.fire({
      title: "Reject Purchase Order",
      text: `Are you sure you want to reject purchase order "${order.po_number}"?`,
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Enter rejection reason",
      inputAttributes: {
        "aria-label": "Rejection reason",
      },
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, reject it!",
      inputValidator: (value) => {
        if (!value) {
          return "Rejection reason is required!";
        }
      },
    });

    if (result.isConfirmed) {
      try {
        const rejectionReason = result.value;
        const userData = localStorage.getItem("user");
        const user = userData ? JSON.parse(userData) : null;

        const payload = {
          rejected_by: user?.id || "",
          rejection_reason: rejectionReason,
          rejection_date: new Date().toISOString(),
        };

        const response = await fetch(
          `${API_URL}/purchase-orders/${order.id}/reject`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {}
          
          if (errorData?.errors) {
            const errorMessages = Object.values(errorData.errors).flat().join('\\n');
            throw new Error(errorMessages);
          }
          throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        }

        // const data = await response.json();
        Swal.fire("Rejected!", "Purchase order has been rejected.", "success");
        refetch();
        refetchStatistics();
      } catch (error) {
        console.error("Error rejecting purchase order:", error);
        Swal.fire("Error!", error.message || "Failed to reject purchase order.", "error");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID");
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "ordered":
        return "info";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handlePrintPDF = async () => {
    if (!viewOrder) return;

    try {
      // Create print content
      const printContent = document.createElement("div");
      printContent.innerHTML = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
          <!-- Header with Logo -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
            <img src="/new-logo.jpeg" alt="Kuber Logo" style="max-width: 150px; height: auto; margin-bottom: 10px;" />
            <h1 style="margin: 0; color: #333; font-size: 24px;">PURCHASE ORDER</h1>
            <p style="margin: 5px 0; color: #666; font-size: 14px;">Procurement Document</p>
          </div>

          <!-- Order Information -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
            <div style="flex: 1; margin-right: 20px;">
              <h3 style="margin-bottom: 10px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Order Information</h3>
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 5px 0; font-weight: bold; width: 120px;">Order Number:</td>
                  <td style="padding: 5px 0;">${viewOrder.po_number || ""}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0; font-weight: bold;">Order Date:</td>
                  <td style="padding: 5px 0;">${formatDate(
                    viewOrder.created_at
                  )}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0; font-weight: bold;">Status:</td>
                  <td style="padding: 5px 0;">
                    <span style="background-color: ${
                      viewOrder.status?.toLowerCase() === "pending"
                        ? "#ffc107"
                        : viewOrder.status?.toLowerCase() === "ordered"
                        ? "#17a2b8"
                        : viewOrder.status?.toLowerCase() === "rejected"
                        ? "#dc3545"
                        : "#6c757d"
                    }; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px;">
                      ${viewOrder.status || "Unknown"}
                    </span>
                  </td>
                </tr>
              </table>
            </div>
            <div style="flex: 1;">
              <h3 style="margin-bottom: 10px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Parties Involved</h3>
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 5px 0; font-weight: bold; width: 100px;">Warehouse:</td>
                  <td style="padding: 5px 0;">${
                    viewOrder.warehouse?.name || ""
                  }</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0; font-weight: bold;">Requester:</td>
                  <td style="padding: 5px 0;">${
                    viewOrder.requester?.name || ""
                  }</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0; font-weight: bold;">Supplier:</td>
                  <td style="padding: 5px 0;">${
                    viewOrder.supplier?.name || ""
                  }</td>
                </tr>
              </table>
            </div>
          </div>

          <!-- Order Items -->
          <h3 style="margin-bottom: 15px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Order Items</h3>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Item</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">Quantity</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Unit Price (Rp)</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Total Price (Rp)</th>
              </tr>
            </thead>
            <tbody>
              ${
                viewOrder.items
                  ?.map(
                    (item, index) => `
                <tr ${
                  index % 2 === 0 ? 'style="background-color: #f9f9f9;"' : ""
                }>
                  <td style="border: 1px solid #ddd; padding: 8px;">${
                    item.inventory?.name || `Item ${item.inventory_id}`
                  }</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${
                    item.quantity_ordered
                  }</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatNumber(
                    item.unit_price
                  )}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatNumber(
                    item.total_price
                  )}</td>
                </tr>
              `
                  )
                  .join("") || ""
              }
            </tbody>
            <tfoot>
              <tr style="background-color: #e9ecef; font-weight: bold;">
                <td colspan="3" style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">TOTAL:</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">${formatNumber(
                  viewOrder.total_amount
                )}</td>
              </tr>
            </tfoot>
          </table>

          <!-- Notes -->
          ${
            viewOrder.note
              ? `
            <div style="margin-bottom: 30px;">
              <h3 style="margin-bottom: 10px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Notes</h3>
              <p style="margin: 0; padding: 10px; background-color: #f8f9fa; border-left: 4px solid #007bff;">${viewOrder.note}</p>
            </div>
          `
              : ""
          }

          <!-- Footer -->
          <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
            <p>This document was generated on ${new Date().toLocaleDateString(
              "id-ID",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }
            )}</p>
            <p>KUBER - Workshop Management System</p>
          </div>
        </div>
      `;

      // Configure PDF options
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5], // top, left, bottom, right in inches
        filename: `Purchase_Order_${viewOrder.po_number || "Unknown"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "in",
          format: "a4",
          orientation: "portrait",
        },
      };

      // Generate and download PDF
      await html2pdf().set(opt).from(printContent).save();

      Swal.fire(
        "Success!",
        "PDF has been generated and downloaded.",
        "success"
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      Swal.fire("Error!", "Failed to generate PDF.", "error");
    }
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-3 workshop-page-scope">
        <WorkshopTopbar />
        <div
          className="workshop-content-panel"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          {showModal ? (
            <WarehouseProvider>
              <WorkshopSupplierProvider>
                <InventoryProvider>
                  <CategoryProvider>
                    <PurchaseOrderForm
                      show={showModal}
                      onHide={handleModalClose}
                      initialData={selectedOrder}
                      onSuccess={handleFormSuccess}
                      onError={handleFormError}
                    />
                  </CategoryProvider>
                </InventoryProvider>
              </WorkshopSupplierProvider>
            </WarehouseProvider>
          ) : (
            <>
              {/* Header */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="h4 h-md-h2 mb-1 text-dark">
                Purchase Orders Management
              </h2>
              <p className="text-muted small mb-0">
                Manage workshop purchase orders and suppliers
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <PermissionMiddleware allowedRoles={["superadmin", "mekanik"]}>
                <Button
                  variant="success"
                  onClick={handleCreateOrder}
                  className="d-flex align-items-center gap-1"
                >
                  <IconPlus size={16} />
                  <span className="d-none d-sm-inline">
                    Create Purchase Order
                  </span>
                </Button>
              </PermissionMiddleware>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="row mb-4">
            {[
              { value: stats.total, label: "Total Orders", color: "primary" },
              { value: stats.pending, label: "Pending", color: "warning" },
              { value: stats.ordered, label: "Ordered", color: "info" },
              { value: stats.rejected, label: "Rejected", color: "danger" },
              {
                value: stats.completed,
                label: "Completed",
                color: "secondary",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className={`${
                  index === 0 ? "col-md-4" : "col-md-2"
                } col-sm-4 mb-3`}
              >
                <div className="card h-100">
                  <div className="card-body text-center">
                    <div className="mb-2">
                      <h1 className={`fs-2 card-title text-${stat.color} mb-1`}>
                        {stat.value.count}
                      </h1>
                      <p className="card-text small text-muted mb-1">
                        {stat.label}
                      </p>
                    </div>
                    <div className="border-top pt-2">
                      <p className="card-text small mb-0 text-dark">
                        <strong>Rp {formatNumber(stat.value.amount)}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="row mb-4">
            <div className="col-md-6 col-lg-4 mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <IconSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by order number, supplier, or requester..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearch}
                />
              </InputGroup>
            </div>
            <div className="col-md-6 col-lg-2 mb-3">
              <Form.Select
                value={filterOptions.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <Form.Control
                type="date"
                placeholder="Date From"
                value={filterOptions.date_from}
                onChange={(e) => handleDateFromFilter(e.target.value)}
              />
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <Form.Control
                type="date"
                placeholder="Date To"
                value={filterOptions.date_to}
                onChange={(e) => handleDateToFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Error Display */}
          {purchaseOrders.error && (
            <Alert variant="danger" style={{ marginBottom: "24px" }}>
              <strong>Error loading purchase orders:</strong>{" "}
              {purchaseOrders.error}
            </Alert>
          )}

          {/* Purchase Orders Table */}
          <PurchaseOrderTable
            purchaseOrders={purchaseOrders}
            onEdit={handleEditOrder}
            onDelete={handleDeleteOrder}
            onView={handleViewOrder}
            onApprove={handleApproveOrder}
            onReject={handleRejectOrder}
            Pagination={Pagination}
          />
            </>
          )}
        </div>
      </div>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={handleViewModalClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Purchase Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewOrder && (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5>Order Information</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Order Number:</strong>
                        </td>
                        <td>{viewOrder.po_number}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Order Date:</strong>
                        </td>
                        <td>{formatDate(viewOrder.created_at)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Status:</strong>
                        </td>
                        <td>
                          <Badge
                            bg={getStatusBadgeVariant(viewOrder.status)}
                            style={{
                              color: getContrastColor(
                                getStatusBadgeVariant(viewOrder.status)
                              ),
                            }}
                          >
                            {viewOrder.status || "Unknown"}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5>Parties Involved</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Warehouse:</strong>
                        </td>
                        <td>{viewOrder.warehouse?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Requester:</strong>
                        </td>
                        <td>{viewOrder.requester?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Supplier:</strong>
                        </td>
                        <td>{viewOrder.supplier?.name}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h5>Order Items</h5>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th className="text-end">Unit Price (Rp)</th>
                      <th className="text-end">Total Price (Rp)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewOrder.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {item.inventory?.name || `Item ${item.inventory_id}`}
                        </td>
                        <td>{item.quantity_ordered}</td>
                        <td className="text-end">
                          {formatNumber(item.unit_price)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.total_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="row mt-4">
                <div className="col-md-6">
                  <h5>Notes</h5>
                  <p>{viewOrder.note || "No notes"}</p>
                </div>
                <div className="col-md-6 text-end">
                  <div className="border p-3 rounded">
                    <p className="mb-0">
                      Total (Rp):{" "}
                      <strong>{formatNumber(viewOrder.total_amount)}</strong>
                    </p>
                  </div>
                </div>
              </div>
              <PrintJson
                data={viewOrder}
                title="Purchase Order Detail JSON Data"
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handlePrintPDF}
            className="d-flex align-items-center gap-1"
          >
            <IconPrinter size={16} />
            Print PDF
          </Button>
          <Button variant="secondary" onClick={handleViewModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <PrintJson data={purchaseOrders} title="Purchase Order JSON Data" />
    </LayoutDashboard>
  );
}
